import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';

import { translate } from '@jsverse/transloco';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { v4 as UUIDv4 } from 'uuid';

import { DropdownObject, EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { RGBAColor } from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { AppSharedModule } from 'app/app-shared.module';
import { AppService } from 'app/app.service';
import { Comparison, ComparisonFilterSet } from 'app/providers/bridge-api/dashboard.service';
import { DEFAULT_PROJECT_COLOR_PALETTE } from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { generalNameValidator } from 'app/utils/customValidators';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';

import { Utils } from '../../../../utils/utils';
import { ComparisonFilterItemComponent } from '../comparison-filter-item/comparison-filter-item.component';
import { CreateComparisonFilterDialogComponent } from '../create-comparison-filter-dialog/create-comparison-filter-dialog.component';
import { ChartCustomColorService } from '../../services/chart-custom-color.service';
import { DEFAULT_COMPARISON_COLOR } from '../../services/comparison-handler-service';
import { DatasourceManagerStore, DatasourcesType } from '../../stores/datasource-manager.store';

export type ComparisonType = 'date' | 'keyword' | 'numeric' | 'sectionFilters';
export type ComparisonSubType = 'sectionDate' | 'sectionKeyword' | 'sectionNumeric';
export type ComparisonDateType = 'regularDate' | 'dynamicDate';
export interface ComparisonFilterExtraDropdown {
  placeholder: string;
  value?: any;
  data: DropdownObject[];
  onValueChange?: (value: any) => void;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-create-comparison-drawer',
  imports: [AppSharedModule, CreateComparisonFilterDialogComponent, ComparisonFilterItemComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './create-comparison-drawer.component.html',
  styleUrls: ['./create-comparison-drawer.component.scss'],
})
export class CreateComparisonDrawerComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() open: boolean;
  @Input() isReadOnly: boolean;
  @Input() projectId;
  @Input() pageId;
  @Input() comparison?: Comparison;
  @Input() comparisonList: Comparison[] = [];

  @Input()
  get projectColors(): string[] {
    return this._projectColors;
  }
  set projectColors(value: string[]) {
    this._projectColors = value;
    if (value) {
      this.setColorPalette(value);
    }
  }

  @Output() close = new EventEmitter();

  readonly datasourceManagerStore = inject(DatasourceManagerStore);

  dataSources: DatasourcesType;
  selectableDataSources: DatasourcesType;
  name: string;
  existingComparisonNames: string[];
  nameStatus = ErrorObjectUtils.create(EnumErrorStates.ERROR);
  filterLabelStatuses: ErrorObject[] = [];
  existingFilterLabels: { id: string; label: string }[];
  color: RGBAColor = DEFAULT_COMPARISON_COLOR; //XIColors.orca.primary;
  colorChange$ = new Subject<RGBAColor>();
  destroy$ = new Subject<void>();

  type: ComparisonType;
  dateType: ComparisonDateType;
  comparisonTypes: DropdownObject[] = [
    {
      id: 'date',
      title: translate('create-comparison-drawer-component.date'),
    },
    {
      id: 'keyword',
      title: translate('create-comparison-drawer-component.keyword'),
    },
    {
      id: 'numeric',
      title: translate('create-comparison-drawer-component.numeric'),
    },
    {
      id: 'sectionFilters',
      title: translate('create-comparison-drawer-component.sectionFilters'),
    },
  ];
  filters: ComparisonFilterSet[] = [];
  filtersColors: RGBAColor[] = [DEFAULT_COMPARISON_COLOR];
  defaultColorPalette: RGBAColor[];
  projectColorPalette: { color: RGBAColor }[];
  activeFilterColor: RGBAColor;

  selectedDataSourceId;
  selectedDataField;
  showCreateComparisonModal;

  isNameValid: boolean;
  areLabelsValid = true;
  isDynamicDateValid = true;
  isAddFilterPopoutVisible = false;

  private _projectColors: string[];

  constructor(private alertService: AlertService) {}

  ngOnInit(): void {
    AppService.hideBodyOverflow();

    this.existingComparisonNames = this.comparisonList.map((c) => c.title);

    if (this.comparison) {
      this.name = this.comparison.title;
      this.color = ChartCustomColorService.colorStringToRgbaColor(this.comparison.color);
      this.type = this.comparison.type;
      this.filters = JSON.parse(JSON.stringify(this.comparison.filters));
      this.existingFilterLabels = this.filters.map((f) => ({ id: f.id, label: f.label }));
      this.selectedDataSourceId = this.comparison.lastUsedDataSource;
      this.selectedDataField = this.comparison.lastUsedDataField;
      this.existingComparisonNames = this.existingComparisonNames.filter(
        (c) => c !== this.comparison.title
      );
      this.filtersColors = this.filters.map((f) =>
        ChartCustomColorService.colorStringToRgbaColor(f.color ?? 'rgba(37,55,77,1)')
      );

      this.onValidateName();
      this.validateFilterLabels();
    }
    this.datasourceManagerStore.loadDataSources(this.projectId).then(() => {
      this.dataSources = this.datasourceManagerStore.getDatasources();
      this.refreshSelectableDatasources();
    });

    this.colorChange$
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => Utils.deepEquals(prev, curr))
      )
      .subscribe((value) => this.onItemColorChange(value));

    this.setColorPalette(this.projectColors);
  }

  private validateFilterLabels() {
    this.filterLabelStatuses = [];
    for (const [index, filter] of this.filters.entries()) {
      this.filterLabelStatuses.push(ErrorObjectUtils.create(EnumErrorStates.ERROR));
      this.onValidateFilterLabel(filter, index);
    }
  }

  private setColorPalette(colors: string[]): void {
    this.defaultColorPalette = (colors ?? DEFAULT_PROJECT_COLOR_PALETTE).map((colorString) =>
      ChartCustomColorService.colorStringToRgbaColor(colorString)
    );

    this.projectColorPalette = this.defaultColorPalette.map((color) => ({ color }));

    if (!this.comparison) {
      this.filtersColors = this.defaultColorPalette;
    }
  }

  onValidateName() {
    generalNameValidator(
      this.name,
      this.nameStatus,
      translate('create-comparison-drawer-component.comparison'),
      64,
      true,
      this.existingComparisonNames,
      false
    );

    this.isNameValid = !this.nameStatus.state || this.nameStatus.state === 'success';
  }

  onValidateFilterLabel(filter: ComparisonFilterSet, index: number) {
    this.areLabelsValid = true;
    generalNameValidator(
      filter.label,
      this.filterLabelStatuses[index],
      translate('create-comparison-drawer-component.filter'),
      64,
      true,
      // We need to check the currently up to date list of filters, existingFilterLabels is not updated yet when this function is called
      this.filters.filter((f) => f.id !== filter.id).map((i) => i.label),
      false
    );
    for (const status of this.filterLabelStatuses) {
      if (status.state === EnumErrorStates.ERROR || status.state === EnumErrorStates.WARNING) {
        this.areLabelsValid = false;
      }
    }
  }

  private validateDynamicDate() {
    this.isDynamicDateValid =
      this.type !== 'date' ||
      (this.type === 'date' && !this.hasDynamicDate) ||
      this.dynamicDateRangeEnabled;
  }

  onTypeChange(type: ComparisonType) {
    this.type = type;
    this.refreshSelectableDatasources();
  }

  onItemColorChange(rgbaColor: RGBAColor) {
    this.color = rgbaColor;
  }

  onFilterColorChange(rgbaColor: RGBAColor, index) {
    this.filters[index].color =
      `rgba(${rgbaColor.red},${rgbaColor.green},${rgbaColor.blue},${rgbaColor.alpha})`;
    this.filtersColors[index] = rgbaColor;
  }

  onAddFilter() {
    this.activeFilterColor =
      this.filtersColors[this.filters.length] ||
      this.defaultColorPalette[this.filters.length % this.defaultColorPalette.length];
    if (this.type === 'date' && this.dateType !== 'dynamicDate') {
      this.isAddFilterPopoutVisible = true;
    } else {
      this.showCreateComparisonModal = true;
    }
  }

  onAddDateFilter(dateRangeType: ComparisonDateType) {
    this.dateType = dateRangeType;
    this.isAddFilterPopoutVisible = false;
    this.showCreateComparisonModal = true;
  }

  refreshSelectableDatasources() {
    let dataSourcesToApply = {
      ...this.dataSources,
    };

    // When sectionFilters is selected, keep only data sources that have sections
    if (this.type === 'sectionFilters') {
      dataSourcesToApply = Object.entries(this.dataSources).reduce(
        (acc, [key, sources]) => {
          const filtered = sources.filter((ds) => ds.hasSections);
          if (filtered.length) {
            acc[key] = filtered;
          }
          return acc;
        },
        {} as typeof this.dataSources
      );
    }

    this.selectableDataSources = {
      ...dataSourcesToApply,
    };
  }

  get dynamicDateRangeEnabled() {
    return this.dateRangeFiltersCount === 1;
  }

  get regularDateRangeEnabled() {
    return this.dateRangeFiltersCount === 0 || !this.hasDynamicDate;
  }

  get hasDynamicDate() {
    return this.filters?.some((f) => !!f?.dynamicDate?.rangeNumber);
  }

  get dateRangeFiltersCount() {
    return this.type === 'date' && this.filters.length
      ? this.filters.filter((f) => !f?.dynamicDate)?.length
      : 0;
  }

  onCreateFilterModalClosed($event) {
    this.showCreateComparisonModal = false;
    this.dateType = null;

    if ($event) {
      this.selectedDataSourceId = $event.dataSourceId;
      this.selectedDataField = $event.dataField;
      const subType: ComparisonSubType = $event.subType;

      this.filters.push($event.filters);

      if (this.type === 'date' || subType === 'sectionDate') {
        this.filters.sort((f1, f2) => {
          const f1Start = f1.and[0].value.replace('daterange/', '').split('/')[0];
          const f2Start = f2.and[0].value.replace('daterange/', '').split('/')[0];
          return new Date(f2Start).getTime() - new Date(f1Start).getTime();
        });
      }
      this.existingFilterLabels = this.filters.map((f) => ({ id: f.id, label: f.label }));
      this.validateFilterLabels();
      this.validateDynamicDate();
      this.filtersColors = this.filters.map((f) =>
        ChartCustomColorService.colorStringToRgbaColor(f.color ?? 'rgba(37,55,77,1)')
      );
    }
  }

  onFiltersRemoved(idx) {
    this.filters.splice(idx, 1);
    this.existingFilterLabels = this.filters.map((f) => ({ id: f.id, label: f.label }));
    this.validateFilterLabels();
    this.validateDynamicDate();
    this.filtersColors.splice(idx, 1);
  }

  cancel() {
    this.close.emit();
  }

  public async save() {
    if (!this.isNameValid || !this.areLabelsValid) {
      return;
    }
    const comparison: Comparison = {
      id: this.comparison?.id ?? UUIDv4(),
      title: this.name,
      type: this.type,
      color: `rgba(${this.color.red},${this.color.green},${this.color.blue},${this.color.alpha})`,
      filters: this.filters,
      page: this.pageId,
      lastUsedDataSource: this.selectedDataSourceId,
      lastUsedDataField: this.selectedDataField,
      widgets: this.comparison?.widgets || [],
    };

    this.alertService.showToast(
      translate('create-comparison-drawer-component.error-message', {
        name: this.name,
        action: this.comparison
          ? translate('create-comparison-drawer-component.updated')
          : translate('create-comparison-drawer-component.created'),
      })
    );

    this.close.emit(comparison);
  }

  ngOnDestroy() {
    AppService.showBodyOverflow();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFiltersChange(filterEvent, idx) {
    //Creating fresh object ref for dynamic date currently causing infinite loop
    this.filters[idx].and = filterEvent.and;
    this.filters[idx].dynamicDate = filterEvent.dynamicDate;
    if (!filterEvent.dynamicDate?.rangeNumber) {
      //trigger refresh of all filters to update dynamic date if regular date changes
      this.filters = [...this.filters];
    }
  }
}
